/**
 * The CMake commands the editor completes, from two sources: CMake's built-in
 * commands, and the functions and macros ESP-IDF defines in the guest's
 * `/idf/tools/cmake` (build system v1) or `/idf/tools/cmakev2` (v2). This
 * module holds no editor code, so the Node tests exercise it directly.
 */

/** A command to complete, with what its definition says about it. */
export interface CmakeCommand {
    name: string;
    /** The parameters as written in the definition: "var property". */
    params: string;
    /** The documentation comment above the definition, without its markup; see `docAbove`. */
    doc: string;
    /** Upper-case argument names from the documentation: SRCS, INCLUDE_DIRS. */
    keywords: string[];
}

const DEFINITION = /^[ \t]*(?:function|macro)[ \t]*\([ \t]*([A-Za-z_][A-Za-z0-9_]*)([^)]*)\)/gim;
const BRACKET_COMMENT = /#\[(=*)\[[\s\S]*?\]\1\]/g;

/**
 * The text with comments blanked out and line numbers kept, so examples in
 * them are not read as code. A "#" inside a string also ends the line here,
 * which at worst hides a property name.
 */
function code(text: string): string {
    return text
        .replace(BRACKET_COMMENT, (comment) => comment.replace(/[^\n]/g, " "))
        .replace(/#.*$/gm, (comment) => " ".repeat(comment.length));
}

/**
 * The functions and macros defined in a CMake file. Names that start with
 * an underscore are ESP-IDF's internal helpers and are left out.
 */
export function definitions(text: string): CmakeCommand[] {
    const lines = text.split("\n");
    const found: CmakeCommand[] = [];
    for (const match of code(text).matchAll(DEFINITION)) {
        const name = match[1]!;
        if (name.startsWith("_")) continue;
        const doc = docAbove(lines, text.slice(0, match.index).split("\n").length - 1, name);
        // "@param[in] SRCS" in v1, "*SRCS[in,opt]*" in v2.
        const keywords = [...doc.matchAll(/(?:@param\[[^\]]*\]\s+|^\s*\*)([A-Z][A-Z0-9_]*)\b/gm)].map((m) => m[1]!);
        found.push({ name, params: match[2]!.trim().replace(/\s+/g, " "), doc, keywords });
    }
    return found;
}

/**
 * The documentation of the command defined on line `line`. v1 writes it as
 * "#" lines starting with the name; v2 as a "#[[api" block holding a
 * ".. cmakev2:function:: name" directive. Any other comment is about
 * something else and gives an empty string.
 */
function docAbove(lines: string[], line: number, name: string): string {
    let doc: string[] = [];
    if (lines[line - 1]?.trim() === "#]]") {
        let start = line - 2;
        while (start >= 0 && !lines[start]!.startsWith("#[[")) start--;
        const body = lines.slice(start + 1, line - 1);
        const directive = new RegExp(`^\\.\\. cmakev2:(?:function|macro):: ${name}\\s*$`);
        if (start < 0 || !directive.test(body[0] ?? "")) return "";
        doc = body.slice(1).filter((l) => !/^\s*\.\. code-block::/.test(l));
    } else {
        for (let i = line - 1; i >= 0 && /^\s*#/.test(lines[i]!); i--) {
            doc.unshift(lines[i]!.replace(/^\s*# ?/, ""));
        }
        if (doc.shift()?.trim() !== name) return "";
    }
    const indent = Math.min(...doc.filter((l) => l.trim() !== "").map((l) => /^ */.exec(l)![0].length));
    return doc
        .map((l) => l.slice(indent).trimEnd())
        .join("\n")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
}

/** The commands that take an ESP-IDF build or component property, and which argument it is. */
const PROPERTY_ARGUMENT: Record<string, { kind: keyof Properties; index: number }> = {
    idf_build_get_property: { kind: "build", index: 1 },
    idf_build_set_property: { kind: "build", index: 0 },
    idf_build_unset_property: { kind: "build", index: 0 },
    idf_component_get_property: { kind: "component", index: 2 },
    idf_component_set_property: { kind: "component", index: 1 },
    __component_get_property: { kind: "component", index: 2 },
    __component_set_property: { kind: "component", index: 1 },
};

/** Build and component property names. */
export interface Properties {
    build: Set<string>;
    component: Set<string>;
}

function propertyArgument(command: string): { kind: keyof Properties; index: number } | undefined {
    const key = command.toLowerCase();
    return Object.hasOwn(PROPERTY_ARGUMENT, key) ? PROPERTY_ARGUMENT[key] : undefined;
}

/** Which kind of property a command takes, if any. */
export function propertyKind(command: string): keyof Properties | undefined {
    return propertyArgument(command)?.kind;
}

/**
 * Add the property names a CMake file documents or passes to the property
 * commands. v1 documents none, so its own calls are the list; v2 documents
 * its public ones as ".. cmakev2:build_property:: NAME". Names that are not
 * upper case, such as variables and the "__" internals, are left out.
 */
export function collectProperties(text: string, into: Properties): void {
    for (const match of text.matchAll(/^\.\. cmakev2:(build|component)_property:: ([A-Z][A-Z0-9_]*)\s*$/gm)) {
        into[match[1] as keyof Properties].add(match[2]!);
    }
    for (const match of code(text).matchAll(/\b([A-Za-z_]+_property)\s*\(([^)]*)\)/g)) {
        const use = propertyArgument(match[1]!);
        if (use === undefined) continue;
        const name = match[2]!.trim().split(/\s+/)[use.index];
        if (name !== undefined && /^[A-Z][A-Z0-9_]*$/.test(name)) into[use.kind].add(name);
    }
}

/**
 * The command whose argument list the offset is in, or undefined. It counts
 * parentheses backwards and does not skip those in comments or strings; a
 * stray one there costs the keyword suggestions and nothing else.
 */
export function enclosingCommand(text: string, offset: number): string | undefined {
    let depth = 0;
    for (let i = offset - 1; i >= 0; i--) {
        const c = text[i];
        if (c === ")") depth++;
        else if (c === "(") {
            if (depth > 0) {
                depth--;
                continue;
            }
            return /([A-Za-z_][A-Za-z0-9_]*)\s*$/.exec(text.slice(Math.max(0, i - 200), i))?.[1];
        }
    }
    return undefined;
}

/** Of two definitions of one command, the one that says more: documented first, then with parameters. */
export function better(a: CmakeCommand, b: CmakeCommand): CmakeCommand {
    const score = (c: CmakeCommand) => (c.doc !== "" ? 2 : 0) + (c.params !== "" ? 1 : 0);
    return score(b) > score(a) ? b : a;
}

/** What one ESP-IDF build system's CMake files define and use. */
export interface IdfCmake {
    commands: Map<string, CmakeCommand>;
    properties: Properties;
}

/** Index the CMake files of one build system, keyed by lower-case command name. */
export function indexCmake(texts: string[]): IdfCmake {
    const commands = new Map<string, CmakeCommand>();
    const properties: Properties = { build: new Set(), component: new Set() };
    for (const text of texts) {
        collectProperties(text, properties);
        for (const command of definitions(text)) {
            const key = command.name.toLowerCase();
            const known = commands.get(key);
            commands.set(key, known === undefined ? command : better(known, command));
        }
    }
    return { commands, properties };
}

/** The v1 files v2 includes, as written in `include(.../../cmake/NAME.cmake)`. */
export function sharedV1Files(v2Texts: string[]): string[] {
    const names = new Set<string>();
    for (const text of v2Texts) {
        for (const match of text.matchAll(/\.\.\/cmake\/([A-Za-z0-9_.-]+\.cmake)\b/g)) names.add(match[1]!);
    }
    return [...names].sort();
}

/**
 * The build system a CMakeLists.txt selects: "v2" if it includes
 * `tools/cmakev2/idf.cmake`, "v1" if it includes `tools/cmake/project.cmake`,
 * undefined if it is not a project's top-level file. Comments are skipped,
 * since v2 projects may mention the v1 file in one.
 */
export function buildSystem(cmakeLists: string): "v1" | "v2" | undefined {
    const text = code(cmakeLists);
    if (/tools\/cmakev2\/idf\.cmake/.test(text)) return "v2";
    if (/tools\/cmake\/project\.cmake/.test(text)) return "v1";
    return undefined;
}

/** CMake's built-in commands, and those of its standard modules that projects call. */
export const BUILTIN_COMMANDS: readonly string[] = [
    "ExternalProject_Add",
    "ExternalProject_Add_Step",
    "ExternalProject_Add_StepDependencies",
    "ExternalProject_Add_StepTargets",
    "ExternalProject_Get_Property",
    "FetchContent_Declare",
    "FetchContent_GetProperties",
    "FetchContent_MakeAvailable",
    "FetchContent_Populate",
    "FetchContent_SetPopulated",
    "add_compile_definitions",
    "add_compile_options",
    "add_custom_command",
    "add_custom_target",
    "add_definitions",
    "add_dependencies",
    "add_executable",
    "add_library",
    "add_link_options",
    "add_subdirectory",
    "add_test",
    "aux_source_directory",
    "block",
    "break",
    "build_command",
    "build_name",
    "cmake_file_api",
    "cmake_host_system_information",
    "cmake_language",
    "cmake_minimum_required",
    "cmake_parse_arguments",
    "cmake_path",
    "cmake_pkg_config",
    "cmake_policy",
    "configure_file",
    "continue",
    "create_test_sourcelist",
    "ctest_build",
    "ctest_configure",
    "ctest_coverage",
    "ctest_empty_binary_directory",
    "ctest_memcheck",
    "ctest_read_custom_files",
    "ctest_run_script",
    "ctest_sleep",
    "ctest_start",
    "ctest_submit",
    "ctest_test",
    "ctest_update",
    "ctest_upload",
    "define_property",
    "else",
    "elseif",
    "enable_language",
    "enable_testing",
    "endblock",
    "endforeach",
    "endfunction",
    "endif",
    "endmacro",
    "endwhile",
    "exec_program",
    "execute_process",
    "export",
    "export_library_dependencies",
    "file",
    "find_file",
    "find_library",
    "find_package",
    "find_package_check_version",
    "find_package_handle_standard_args",
    "find_path",
    "find_program",
    "fltk_wrap_ui",
    "foreach",
    "function",
    "get_cmake_property",
    "get_directory_property",
    "get_filename_component",
    "get_property",
    "get_source_file_property",
    "get_target_property",
    "get_test_property",
    "if",
    "include",
    "include_directories",
    "include_external_msproject",
    "include_guard",
    "include_regular_expression",
    "install",
    "install_files",
    "install_programs",
    "install_targets",
    "link_directories",
    "link_libraries",
    "list",
    "load_cache",
    "load_command",
    "macro",
    "make_directory",
    "mark_as_advanced",
    "math",
    "message",
    "option",
    "output_required_files",
    "project",
    "qt_wrap_cpp",
    "qt_wrap_ui",
    "remove",
    "remove_definitions",
    "return",
    "separate_arguments",
    "set",
    "set_directory_properties",
    "set_property",
    "set_source_files_properties",
    "set_target_properties",
    "set_tests_properties",
    "site_name",
    "source_group",
    "string",
    "subdir_depends",
    "subdirs",
    "target_compile_definitions",
    "target_compile_features",
    "target_compile_options",
    "target_include_directories",
    "target_link_directories",
    "target_link_libraries",
    "target_link_options",
    "target_precompile_headers",
    "target_sources",
    "try_compile",
    "try_run",
    "unset",
    "use_mangled_mesa",
    "utility_source",
    "variable_requires",
    "variable_watch",
    "while",
    "write_file",
];
